import { createSearchParams } from 'react-router';

import { Button, Menu, MenuButton, MenuDivider, MenuList, MenuOptionGroup, Portal } from '@chakra-ui/react';

import { CheckDouble, ChevronDown, HasAccess, HouseUser, Pickaxe } from '@enterprise/ui';

import type { MailingTypeMenuProps } from './types/mailing-type-menu.props';

import { useMailingTypeMenuConfiguration } from './hooks/mailing-type-menu-configuration.hook';
import NavItem from './nav-item';
import RadioMenuItem from './radio-menu-item';

const MailingTypeMenu = ({
  children,
  externalMenuItemsSlot,
  hspMenuItemsSlot,
  isActive,
  label,
  pathSegment,
}: MailingTypeMenuProps) => {
  const { isOpen, onClose, onOpen, selectedMenu } = useMailingTypeMenuConfiguration();

  if (!isActive) {
    return (
      <NavItem
        isActive={false}
        label={label}
      />
    );
  }

  return (
    <Menu
      gutter={0}
      isLazy
      isOpen={isOpen}
      matchWidth>
      <MenuButton
        _expanded={{ bg: 'hsp.500' }}
        _focus={{ boxShadow: 'outline' }}
        as={Button}
        bg="hsp.500"
        borderRadius="md"
        borderWidth="1px"
        color="white"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        p={2}
        rightIcon={<ChevronDown className="ms-1" />}
        rounded="lg">
        {label}
      </MenuButton>
      <Portal>
        <MenuList
          borderColor="hsp.500"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}>
          <MenuOptionGroup title="HSP">
            <RadioMenuItem
              icon={
                <CheckDouble
                  fixedWidth
                  size="lg"
                />
              }
              isSelected={selectedMenu === 'internal-all'}
              replace
              state={{ selectedMenu: 'internal-all' }}
              to={`/datastream/${pathSegment}`}>
              All
            </RadioMenuItem>
            <RadioMenuItem
              icon={
                <Pickaxe
                  fixedWidth
                  size="lg"
                />
              }
              isSelected={selectedMenu === 'internal-prospect'}
              replace
              state={{ selectedMenu: 'internal-prospect' }}
              to={{
                pathname: `/datastream/${pathSegment}`,
                search: `?${createSearchParams({ tags: 'MailingType:Prospect' })}`,
              }}>
              Prospect
            </RadioMenuItem>
            <RadioMenuItem
              icon={
                <HouseUser
                  fixedWidth
                  size="lg"
                />
              }
              isSelected={selectedMenu === 'internal-house'}
              replace
              state={{ selectedMenu: 'internal-house' }}
              to={{
                pathname: `/datastream/${pathSegment}`,
                search: `?${createSearchParams({ tags: 'MailingType:House' })}`,
              }}>
              House
            </RadioMenuItem>
            {hspMenuItemsSlot?.({ pathSegment, selectedMenu })}
          </MenuOptionGroup>
          <HasAccess permissions={['dts-view-mailings-external']}>
            <MenuDivider />
            <MenuOptionGroup title="External">
              <RadioMenuItem
                icon={
                  <CheckDouble
                    fixedWidth
                    size="lg"
                  />
                }
                isSelected={selectedMenu === 'external-all'}
                replace
                state={{ selectedMenu: 'external-all' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                }}>
                All
              </RadioMenuItem>
              <RadioMenuItem
                icon={
                  <Pickaxe
                    fixedWidth
                    size="lg"
                  />
                }
                isSelected={selectedMenu === 'external-prospect'}
                replace
                state={{ selectedMenu: 'external-prospect' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                  search: `?${createSearchParams({ tags: 'MailingType:External Prospect' })}`,
                }}>
                Prospect
              </RadioMenuItem>
              <RadioMenuItem
                icon={
                  <HouseUser
                    fixedWidth
                    size="lg"
                  />
                }
                isSelected={selectedMenu === 'external-house'}
                replace
                state={{ selectedMenu: 'external-house' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                  search: `?${createSearchParams({ tags: 'MailingType:External House' })}`,
                }}>
                House
              </RadioMenuItem>
              {externalMenuItemsSlot?.({ pathSegment, selectedMenu })}
            </MenuOptionGroup>
          </HasAccess>
          {children}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default MailingTypeMenu;
